import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Tooltip from "@mui/material/Tooltip";
import { MdsSort } from "@sinch/icons";
import React, { FC, MouseEventHandler, ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";
import { MdsIcon } from "../../../MdsIcon/MdsIcon";
import { AttendanceSortField } from "./attendanceSort";
import ListSubheader from "@mui/material/ListSubheader";

interface AttendanceSortMenuProps {
  value: AttendanceSortField | null;
  onChange: (field: AttendanceSortField) => void;
}

/**
 * Small button with menu for client side sorting of rows in attendance table
 */
export const AttendanceSortMenu: FC<AttendanceSortMenuProps> = ({ value, onChange }) => {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const options: { field: AttendanceSortField; label: string }[] = [
    { field: AttendanceSortField.Surname, label: t("Attendance.sortBySurname") },
    { field: AttendanceSortField.Name, label: t("Attendance.sortByFirstName") },
    { field: AttendanceSortField.Rating, label: t("Attendance.sortByRating") },
    { field: AttendanceSortField.Created, label: t("Attendance.sortBySignUp") },
  ];

  const handleOpen: MouseEventHandler<HTMLButtonElement> = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSelect = (field: AttendanceSortField) => () => {
    onChange(field);
    handleClose();
  };

  return (
    <>
      <Tooltip title={t("Attendance.sortBy")}>
        <IconButton onClick={handleOpen} size="small" sx={{ padding: 0, mr: 1 }}>
          <MdsIcon color="info" fontSize="small" icon={MdsSort} />
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={anchorEl}
        anchorOrigin={{ horizontal: "left", vertical: "bottom" }}
        onClose={handleClose}
        open={anchorEl !== null}
        transformOrigin={{ horizontal: "left", vertical: "top" }}
      >
        <SortSubheader>{t("Attendance.sortBy")}</SortSubheader>
        {options.map(({ field, label }) => (
          <MenuItem key={field} onClick={handleSelect(field)} selected={value === field}>
            {label}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

/**
 * Header of the sort menu.
 *
 * `ListSubheader` carries the app-wide grey header band, which is too heavy inside a popover, so it is
 * toned down to a plain caption (same approach as the group header of `NewJobForm/components/GroupSelectInput`).
 */
function SortSubheader({ children }: { children: ReactNode }) {
  return (
    <ListSubheader
      disableSticky
      sx={(theme) => ({
        backgroundColor: "transparent",
        borderBottom: "none",
        color: theme.palette.grey[500],
        fontSize: theme.typography.pxToRem(11),
        letterSpacing: "0.06em",
        lineHeight: "28px",
        textTransform: "uppercase",
      })}
    >
      {children}
    </ListSubheader>
  );
}
